import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSocket } from "@/hooks/useSocket";
import { AppDispatch } from "@/store/store";
import { SocketEvents } from "@/socket/events";
import {
    setIncomingCall,
    setActiveCall,
    setAdminStats,
    setWindowOpen,
    setCallError,
    endCallCleanup,
    adminCallCreated,
    adminCallAccepted,
    adminCallRejected,
    adminCallMissed,
    adminCallEnded,
    adminCallCancelled,
    updateRoutingState,
    updateOnlineUsers,
    clearRoutingState,
    addHistoryItem,
    updateHistoryItemStatus
} from "@features/call/store/callSlice";
import {
    onAdminUiSync,
    onAdminJoinedCall,
} from "@/socket/listeners";

const CallSocketManager: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { socket: socketClient } = useSocket();

    useEffect(() => {
        if (!socketClient) return;

        // --- Admin Event Handlers ---
        const handleAdminSync = (payload: any) => {
            console.log("[SocketManager] Admin Sync:", payload);
            dispatch(setAdminStats(payload));
        };

        const handleAdminCallCreated = (payload: any) => {
            console.log("[SocketManager] Admin Call Created:", payload);
            dispatch(adminCallCreated(payload));
        };

        const handleAdminCallAccepted = (payload: any) => {
            console.log("[SocketManager] Admin Call Accepted:", payload);
            dispatch(adminCallAccepted(payload));
        };

        const handleAdminCallRejected = (payload: any) => {
            console.log("[SocketManager] Admin Call Rejected:", payload);
            dispatch(adminCallRejected(payload));
        };

        const handleAdminCallMissed = (payload: any) => {
            console.log("[SocketManager] Admin Call Missed:", payload);
            dispatch(adminCallMissed(payload));
        };

        const handleAdminCallEnded = (payload: any) => {
            console.log("[SocketManager] Admin Call Ended:", payload);
            dispatch(adminCallEnded(payload));
        };

        const handleAdminCallCancelled = (payload: any) => {
            console.log("[SocketManager] Admin Call Cancelled:", payload);
            dispatch(adminCallCancelled(payload));
        };

        const handleRoutingUpdate = (payload: any) => {
            console.log("[SocketManager] Routing Update:", payload);
            dispatch(updateRoutingState(payload));
        };

        const handleUserStatusUpdate = (payload: any) => {
            console.log("[SocketManager] User Status Update:", payload);
            dispatch(updateOnlineUsers({
                userId: payload.userId,
                status: payload.status,
                role: payload.role,
                name: payload.name
            }));
        };

        // --- User/Curator Event Handlers ---
        const handleIncomingCall = (payload: any) => {
            console.log("[SocketManager] Incoming Call:", payload);
            dispatch(setIncomingCall({
                id: payload.request_id,
                sender_id: payload.initiator_id,
                sender_name: payload.initiator_name, // Verify payload matches backend
                sender_avatar: payload.initiator_avatar,
                sender_role: payload.initiator_role,
                receiver_id: "", // Filled by context if needed
                call_type: payload.call_type,
                status: "ringing",
                timestamp: payload.timestamp || new Date().toISOString()
            }));

            // Hybrid Approach: Inject into history immediately
            dispatch(addHistoryItem({
                id: payload.request_id,
                customer_name: payload.initiator_name,
                customer_avatar: payload.initiator_avatar,
                last_call_time: payload.timestamp || new Date().toISOString(),
                status: 'ringing', // Show as ringing until answered/rejected/missed
                call_type: payload.call_type
            }));
        };

        const handleCallAccepted = (payload: any) => {
            console.log("[SocketManager] Call Accepted:", payload);
            // In a real scenario, we might want to merge with incomingCall if we are the curator
            // Or use stored recipient info if we are the customer
            // For now, we populate with placeholders or partial data that will be updated by Admin Sync or UI context

            // Try to find name from payload if it exists (it doesn't in spec, but maybe we change spec later)
            // Defaulting to "Connected User" to avoid UI crash
            dispatch(setActiveCall({
                id: payload.call_id,
                room_name: payload.room_name,
                token: payload.token,
                ws_url: payload.ws_url,
                participants: [],
                started_at: new Date().toISOString(),
                call_type: "video",
                callRequestId: payload.request_id,

                // UI Fields Defaults
                name: "Connecting...", // Should be updated
                initials: "??",
                status: "Active",
                curator: "System",
                purpose: "Video Call",
                time: "00:00",
                location: "Unknown",
                email: "unknown@example.com",
                phone: "",
                memberSince: new Date().toISOString(),
                interests: [],
                rating: 0,
                avatarColor: "bg-gray-500"
            }));
            dispatch(setIncomingCall(null));

            // Update history item to show active status
            dispatch(updateHistoryItemStatus({
                id: payload.request_id || payload.call_id,
                status: 'active'
            }));
        };

        // Also handle accepted by recipient (same logic for the caller)
        const handleCallAcceptedByRecipient = (payload: any) => {
            handleCallAccepted(payload);
        };

        const handleCallRejected = (payload: any) => {
            console.log("[SocketManager] Call Rejected:", payload);
            dispatch(setIncomingCall(null));

            // Update history
            dispatch(updateHistoryItemStatus({
                id: payload.request_id,
                status: 'rejected'
            }));
        };

        const handleCallEnded = (payload: any) => {
            console.log("[SocketManager] Call Ended:", payload);
            dispatch(endCallCleanup());

            // Update history if we have the ID. 
            // Note: payload.call_id might match the request_id depending on backend logic.
            // If they differ, we might need a mapping, but usually simple systems keep them synced or related.
            // Update history if we have the ID. 
            dispatch(updateHistoryItemStatus({
                id: payload.request_id || payload.call_id,
                status: 'completed',
                duration: payload.duration_seconds
            }));
        };

        const handleCallAcceptError = (payload: any) => {
            console.error("[SocketManager] Call Accept Error:", payload);
            dispatch(setCallError(payload.error || payload.message || "Failed to join call"));

            // CRITICAL: Cleanup UI state on accept error
            dispatch(endCallCleanup());

            // Extract request_id from nested details if present (backend parity)
            const requestId = payload.details?.request_id || payload.request_id;

            // Update history if request_id is present to stop the 'ringing' state
            if (requestId) {
                dispatch(updateHistoryItemStatus({
                    id: requestId,
                    status: 'missed'
                }));
            }
        };

        const handleCallError = (payload: any) => {
            console.error("[SocketManager] Call Error:", payload);
            dispatch(setCallError(payload.error || payload.message || "Unknown error"));
        };

        const handleCallTimeout = (payload: any) => {
            console.log("[SocketManager] Call Timeout:", payload);
            dispatch(setIncomingCall(null));

            // Update history to show missed status
            dispatch(updateHistoryItemStatus({
                id: payload.request_id,
                status: 'missed'
            }));
        };

        const handleCallCancelled = (payload: any) => {
            console.log("[SocketManager] Call Cancelled:", payload);
            dispatch(setIncomingCall(null));

            // Update history to show rejected (or cancelled if we add it)
            dispatch(updateHistoryItemStatus({
                id: payload.request_id,
                status: 'rejected'
            }));
        };

        const handleCallMissed = (payload: any) => {
            console.log("[SocketManager] Call Missed:", payload);
            dispatch(setIncomingCall(null));

            // Update history to show missed
            dispatch(updateHistoryItemStatus({
                id: payload.request_id,
                status: 'missed'
            }));
        };

        const handleRejoinAvailable = (payload: any) => {
            console.log("[SocketManager] Rejoin Available:", payload);
            // Prompt user or auto-rejoin?
            // For now auto-emit rejoin
            socketClient.emit(SocketEvents.CALL_REJOIN, { call_id: payload.call_id });
        };

        const handleRejoined = (payload: any) => {
            console.log("[SocketManager] Rejoined:", payload);
            dispatch(setActiveCall({
                id: payload.call_id,
                room_name: payload.room_name,
                token: payload.token,
                ws_url: payload.ws_url,
                participants: [],
                started_at: new Date().toISOString(),
                call_type: "video",
                // UI Fields
                name: "Rejoined Call",
                initials: "RC",
                status: "Active",
                curator: "System",
                purpose: "Rejoined Session",
                time: "00:00",
                location: "Unknown",
                email: "unknown@example.com",
                phone: "",
                memberSince: new Date().toISOString(),
                interests: [],
                rating: 0,
                avatarColor: "bg-blue-500"
            }));
        };



        // Admin
        const unsubAdminSync = onAdminUiSync((payload) => {
            console.log("[SocketManager] Admin Sync:", payload);
            dispatch(setAdminStats(payload));
        });

        const unsubAdminJoinedCall = onAdminJoinedCall((payload) => {
            console.log("[SocketManager] Admin Joined Call:", payload);
            dispatch(setActiveCall({
                id: payload.call_id,
                room_name: payload.room_name,
                token: payload.token,
                ws_url: payload.ws_url,
                participants: [],
                started_at: new Date().toISOString(),
                call_type: "video", // Default to video or infer from context? Payload doesn't have call_type?
                // `AdminJoinedCallPayload` has mode="hidden", but not call_type.
                // We'll assume video or try to get it.
                // UI Fields
                name: "Monitored Call",
                initials: "MC",
                status: "Active",
                curator: "System",
                purpose: "Monitoring",
                time: "00:00",
                location: "Unknown",
                email: "unknown@example.com",
                phone: "",
                memberSince: new Date().toISOString(),
                interests: [],
                rating: 0,
                avatarColor: "bg-purple-500"
            }));
        });

        socketClient.on(SocketEvents.ADMIN_CALL_CREATED, handleAdminCallCreated);
        socketClient.on(SocketEvents.ADMIN_CALL_ACCEPTED, handleAdminCallAccepted);
        socketClient.on(SocketEvents.ADMIN_CALL_REJECTED, handleAdminCallRejected);
        socketClient.on(SocketEvents.ADMIN_CALL_MISSED, handleAdminCallMissed);
        socketClient.on(SocketEvents.ADMIN_CALL_ENDED, handleAdminCallEnded);
        socketClient.on(SocketEvents.ADMIN_CALL_CANCELLED, handleAdminCallCancelled);
        socketClient.on(SocketEvents.ADMIN_ROUTING_UPDATE, handleRoutingUpdate);
        socketClient.on(SocketEvents.UPDATE_USER_STATUS, handleUserStatusUpdate);

        // General
        socketClient.on(SocketEvents.CALL_INCOMING, handleIncomingCall);
        socketClient.on(SocketEvents.CALL_ACCEPTED, handleCallAccepted);
        socketClient.on(SocketEvents.CALL_ACCEPTED_BY_RECIPIENT, handleCallAcceptedByRecipient);
        socketClient.on(SocketEvents.CALL_REJECTED, handleCallRejected);
        socketClient.on(SocketEvents.CALL_REJECTED_CONFIRMED, handleCallRejected);
        socketClient.on(SocketEvents.CALL_REJECTED_BY_RECIPIENT, handleCallRejected); // Same logic
        socketClient.on(SocketEvents.CALL_ENDED, handleCallEnded);
        socketClient.on(SocketEvents.CALL_TIMEOUT, handleCallTimeout);
        socketClient.on(SocketEvents.CALL_CANCELLED, handleCallCancelled);
        socketClient.on(SocketEvents.CALL_CANCELLED_BY_INITIATOR, handleCallCancelled);
        socketClient.on(SocketEvents.CALL_MISSED, handleCallMissed);

        // Errors
        socketClient.on(SocketEvents.CALL_INITIATE_ERROR, handleCallError);
        socketClient.on(SocketEvents.CALL_ACCEPT_ERROR, handleCallAcceptError);
        socketClient.on(SocketEvents.CALL_REJECT_ERROR, handleCallError);
        socketClient.on(SocketEvents.CALL_END_ERROR, handleCallError);

        // Rejoin
        socketClient.on(SocketEvents.CALL_REJOIN_AVAILABLE, handleRejoinAvailable);
        socketClient.on(SocketEvents.CALL_REJOIN_RESPONSE, handleRejoined); // Check if this is correct event name from backend
        // Backend spec says: emit by server `call:rejoined` (SocketEvents.CALL_REJOIN_RESPONSE is likely wrong in events.ts if it was mapping to a different string)
        // Checking socket-event-spec.md: Emit by Server `call:rejoined`
        // Checking events.ts: CALL_REJOIN_RESPONSE = "call:rejoin:response" - This MIGHT be wrong.
        // The spec says `call:rejoined`.

        // Let's correct the listener for rejoined
        socketClient.on("call:rejoined", handleRejoined);


        return () => {
            unsubAdminSync();
            unsubAdminJoinedCall();
            socketClient.off(SocketEvents.ADMIN_CALL_CREATED, handleAdminCallCreated);
            socketClient.off(SocketEvents.ADMIN_CALL_ACCEPTED, handleAdminCallAccepted);
            socketClient.off(SocketEvents.ADMIN_CALL_REJECTED, handleAdminCallRejected);
            socketClient.off(SocketEvents.ADMIN_CALL_MISSED, handleAdminCallMissed);
            socketClient.off(SocketEvents.ADMIN_CALL_ENDED, handleAdminCallEnded);
            socketClient.off(SocketEvents.ADMIN_CALL_CANCELLED, handleAdminCallCancelled);
            socketClient.off(SocketEvents.ADMIN_ROUTING_UPDATE, handleRoutingUpdate);
            socketClient.off(SocketEvents.UPDATE_USER_STATUS, handleUserStatusUpdate);

            socketClient.off(SocketEvents.CALL_INCOMING, handleIncomingCall);
            socketClient.off(SocketEvents.CALL_ACCEPTED, handleCallAccepted);
            socketClient.off(SocketEvents.CALL_ACCEPTED_BY_RECIPIENT, handleCallAcceptedByRecipient);
            socketClient.off(SocketEvents.CALL_REJECTED, handleCallRejected);
            socketClient.off(SocketEvents.CALL_REJECTED_CONFIRMED, handleCallRejected);
            socketClient.off(SocketEvents.CALL_REJECTED_BY_RECIPIENT, handleCallRejected);
            socketClient.off(SocketEvents.CALL_ENDED, handleCallEnded);
            socketClient.off(SocketEvents.CALL_TIMEOUT, handleCallTimeout);
            socketClient.off(SocketEvents.CALL_CANCELLED, handleCallCancelled);
            socketClient.off(SocketEvents.CALL_CANCELLED_BY_INITIATOR, handleCallCancelled);
            socketClient.off(SocketEvents.CALL_MISSED, handleCallMissed);
            socketClient.off(SocketEvents.CALL_INITIATE_ERROR, handleCallError);
            socketClient.off(SocketEvents.CALL_ACCEPT_ERROR, handleCallAcceptError);
            socketClient.off(SocketEvents.CALL_REJECT_ERROR, handleCallError);
            socketClient.off(SocketEvents.CALL_END_ERROR, handleCallError);
            socketClient.off(SocketEvents.CALL_REJOIN_AVAILABLE, handleRejoinAvailable);
            socketClient.off("call:rejoined", handleRejoined);
        };
    }, [socketClient, dispatch]);

    return null; // Logic only component
};

export default CallSocketManager;
